import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useAnimationFrame, useMotionValue } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

/**
 * DefileCarousel — bande de photos de défilé qui défile en boucle continue.
 *
 * Les photos sont CHARGÉES DU BACKEND : la liste d'URLs vient de
 * GET /api/settings (namespace `defile`), passée ici via la prop `images`.
 *
 * Comportement : défilement horizontal lent et infini (marquee), pause au
 * survol. Thème Noir/Or, animations framer-motion. Si `images` est vide,
 * le composant ne rend rien (pas de section fantôme).
 */
const SPEED = 40; // pixels par seconde

const DefileCarousel = ({
  images = [],
  title = 'Le Défilé',
  subtitle,
  description = [],
  ctaLabel,
  ctaHref,
}) => {
  const x = useMotionValue(0);
  const trackRef = useRef(null);
  const hovered = useRef(false);
  const [setWidth, setSetWidth] = useState(0);

  // Largeur d'UN jeu d'images (on en rend deux à la suite pour la boucle).
  useEffect(() => {
    if (!trackRef.current) return;
    const measure = () => setSetWidth(trackRef.current.scrollWidth / 2);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [images]);

  // Avance la position image par image, en bouclant sans à-coup.
  useAnimationFrame((_, delta) => {
    if (hovered.current || setWidth === 0) return;
    let next = x.get() - (delta / 1000) * SPEED;
    if (next <= -setWidth) next += setWidth; // wrap : retour au début
    x.set(next);
  });

  const hasImages = images && images.length > 0;
  if (!hasImages && !description?.length) return null;

  const loop = hasImages ? [...images, ...images] : [];

  return (
    <section
      id="nos-evenements"
      className="py-24 md:py-32 bg-background overflow-hidden"
      data-testid="defile-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.span
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-gold text-xs uppercase tracking-widest"
        >
          {subtitle || 'En mouvement'}
        </motion.span>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-heading text-3xl md:text-5xl tracking-tight mt-2"
        >
          {title}
        </motion.h2>

        {description?.length > 0 && (
          <div className="mt-8 max-w-3xl space-y-4">
            {description.map((paragraph, index) => (
              <motion.p
                key={paragraph.slice(0, 24)}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="text-foreground/70 leading-relaxed"
              >
                {paragraph}
              </motion.p>
            ))}
          </div>
        )}

        {ctaLabel && ctaHref && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-8"
          >
            <Link to={ctaHref} className="btn-primary inline-flex items-center gap-2" data-testid="events-cta">
              {ctaLabel}
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        )}
      </div>

      {hasImages && (
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative mt-12"
          onMouseEnter={() => (hovered.current = true)}
          onMouseLeave={() => (hovered.current = false)}
        >
          <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-32 z-10 bg-gradient-to-r from-background to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-32 z-10 bg-gradient-to-l from-background to-transparent" />

          <motion.div ref={trackRef} style={{ x }} className="flex gap-4 md:gap-6 w-max">
            {loop.map((src, i) => (
              <div
                key={`${src}-${i}`}
                className="relative shrink-0 h-[380px] md:h-[520px] overflow-hidden bg-black/5 group"
                data-testid="defile-slide"
              >
                <img
                  src={src}
                  alt={`Événement Maison Ka ${(i % images.length) + 1}`}
                  loading="lazy"
                  className="h-full w-auto object-cover transition-transform duration-700 group-hover:scale-105"
                  draggable={false}
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-gold/10" />
              </div>
            ))}
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default DefileCarousel;
